/**
 * AiChips.js — Context-aware action chips for the AI panel.
 *
 * Three modes:
 *   bpmn  — original BPMN chips (3 escenas: empty / single-element / multi)
 *   rules — DMN chips (PR 2 of THEMIS_DMN_PLAN.md, 3 escenas):
 *           A · DRD without selection
 *           B · DRD with a node selected (decision / input / bkm / kSrc /
 *               annotation / multiple)
 *           C · Editor open (table / literal / context / list / invocation /
 *               relation / function / ml)
 *
 * `getChips(selection, mode, dmnContext)` returns the chip labels to render.
 * `getIntentForChip(label)` maps a label back to its intent.
 * `isNativeIntent(intent)` tells AiAssistant whether to dispatch natively.
 *
 * Naming convention (THEMIS_DMN_PLAN §2.1):
 *   Snake_case `dmn_<verbo>_<objeto>`. Suggested-options chips reuse the
 *   shared `suggest_options` intent and rely on the chip label to disambiguate.
 */

const CHIPS = {
  // ── BPMN — Scenario 1: no selection — full-flow ────────────────────────
  none: [
    '✨ Generar proceso',
    '💬 Explicar proceso',
    '✅ Validar',
    '📄 Documentar',
    '💡 Sugerir mejoras',
  ],

  // ── BPMN — Scenario 2: selection — single element by type ──────────────
  task: [
    '💬 Explicar',
    '✏️ Renombrar',
    '➕ Agregar siguiente paso',
    '🔄 Cambiar tipo',
  ],
  gateway: [
    '💬 Explicar',
    '⚙️ Configurar condiciones',
    '🔀 Cambiar tipo',
  ],
  event: [
    '💬 Explicar',
    '✏️ Renombrar',
    '🔄 Cambiar tipo',
  ],
  flow: [
    '💬 Explicar',
    '✏️ Agregar condición',
  ],
  // Multiple elements selected
  multiple: [
    '💬 Explicar selección',
    '✏️ Modificar grupo',
    '🔗 Conectar nodos',
    '💡 Sugerir',
  ],

  // ── DMN — Escena A · canvas DRD vacío ──────────────────────────────────
  dmn_none: [
    '✨ Generar reglas',
    '💬 Explicar DRD',
    '✅ Validar cobertura',
    '📄 Documentar dossier',
    '💡 Sugerir escenarios',
  ],

  // ── DMN — Escena B · nodo seleccionado en DRD ──────────────────────────
  dmn_decision: [
    '💬 Explicar decisión',
    '✏️ Renombrar',
    '📝 Editar descripción',
    '🔗 Sugerir inputs faltantes',
  ],
  dmn_input: [
    '✏️ Renombrar',
    '🏷️ Definir tipo/dominio',
    '💬 Explicar uso',
  ],
  dmn_bkm: [
    '💬 Explicar lógica',
    '✏️ Renombrar',
  ],
  dmn_knowledgeSource: [
    '✏️ Renombrar',
    '📎 Vincular documento/política',
    '💬 Explicar autoridad',
  ],
  dmn_annotation: [
    '✏️ Reescribir',
    '🌐 Traducir',
  ],
  dmn_multiple: [
    '💬 Explicar selección',
    '🔗 Conectar (Information Req.)',
    '📦 Agrupar en DecisionService',
  ],

  // ── DMN — Escena C · editor abierto ────────────────────────────────────
  dmn_table: [
    '➕ Agregar regla',
    '🧩 Completar cobertura',
    '🎚️ Sugerir hit policy',
    '🔗 Sugerir inputs faltantes',
    '🗣️ Texto → FEEL',
    '💬 Explicar',
  ],
  dmn_literal: [
    '🗣️ Texto → FEEL',
    '🛠️ Corregir sintaxis',
    '💬 Explicar expresión',
  ],
  dmn_context: [
    '➕ Agregar entrada',
    '🗣️ Texto → entradas',
    '💬 Explicar contexto',
  ],
  dmn_list: [
    '➕ Agregar item',
    '🗣️ Texto → items',
    '💬 Explicar lista',
  ],
  dmn_invocation: [
    '🔗 Sugerir bindings',
    '💬 Explicar invocación',
  ],
  dmn_relation: [
    '➕ Agregar fila',
    '📊 Agregar columna',
    '💬 Explicar relación',
  ],
  dmn_function: [
    '📥 Definir parámetros',
    '🗣️ Texto → cuerpo FEEL',
    '💬 Explicar función',
  ],
  // Per THEMIS_ML_DECISION_PLAN §6.2
  dmn_ml: [
    '💬 Explicar decisión ML',
    '🔗 Sugerir bindings desde DRD',
    '🧪 Sugerir fallback',
    '📊 Generar tabla equivalente',
    '🩺 Validar configuración',
  ],
};

/**
 * Single source of truth for chip → intent → native flag (THEMIS_DMN_PLAN
 * §2.7). `native: true` chips run client-side via AiAssistant handlers; the
 * rest hit the `/assist` (or `/dmn-outline*`) backend.
 *
 * Chip labels with the same emoji + text appear in multiple buckets — the
 * chip text alone is the lookup key, so duplicate keys are intentional and
 * collapse here.
 */
const CHIP_INTENTS = {
  // ── BPMN ────────────────────────────────────────────────────────────────
  '✨ Generar proceso':         { intent: 'generate',         native: false },
  '💬 Explicar proceso':        { intent: 'explain',          native: false },
  '✅ Validar':                 { intent: 'validate',         native: false },
  '📄 Documentar':              { intent: 'document',         native: false },
  '💡 Sugerir mejoras':         { intent: 'suggest',          native: false },
  '💬 Explicar':                { intent: 'explain',          native: false },
  '✏️ Renombrar':               { intent: 'suggest_options', native: true  },
  '➕ Agregar siguiente paso':  { intent: 'suggest_options', native: false },
  '🔄 Cambiar tipo':            { intent: 'suggest_options', native: false },
  '⚙️ Configurar condiciones':  { intent: 'modify',          native: false },
  '🔀 Cambiar tipo':            { intent: 'suggest_options', native: false },
  '✏️ Agregar condición':       { intent: 'modify',          native: false },
  '💬 Explicar selección':      { intent: 'explain',          native: false },
  '✏️ Modificar grupo':         { intent: 'modify',          native: false },
  '🔗 Conectar nodos':          { intent: 'suggest_options', native: true  },
  '💡 Sugerir':                 { intent: 'suggest',          native: false },

  // ── DMN — Escena A ──────────────────────────────────────────────────────
  '✨ Generar reglas':           { intent: 'generate',                 native: false },
  '💬 Explicar DRD':             { intent: 'dmn_explain_drd',          native: false },
  '✅ Validar cobertura':        { intent: 'dmn_validate_coverage',    native: false },
  '📄 Documentar dossier':       { intent: 'dmn_document_full',        native: false },
  '💡 Sugerir escenarios':       { intent: 'dmn_suggest_scenarios',    native: false },

  // ── DMN — Escena B ──────────────────────────────────────────────────────
  '💬 Explicar decisión':        { intent: 'explain',                  native: false },
  '📝 Editar descripción':       { intent: 'dmn_edit_desc',            native: false },
  '🔗 Sugerir inputs faltantes': { intent: 'dmn_suggest_inputs',       native: false },
  '🏷️ Definir tipo/dominio':     { intent: 'dmn_set_type',             native: false },
  '💬 Explicar uso':             { intent: 'explain',                  native: false },
  '💬 Explicar lógica':          { intent: 'explain',                  native: false },
  '📎 Vincular documento/política': { intent: 'dmn_link_doc',          native: false },
  '💬 Explicar autoridad':       { intent: 'explain',                  native: false },
  '✏️ Reescribir':               { intent: 'dmn_rewrite',              native: false },
  '🌐 Traducir':                 { intent: 'dmn_translate',            native: false },
  '🔗 Conectar (Information Req.)': { intent: 'suggest_options',       native: true  },
  '📦 Agrupar en DecisionService':  { intent: 'dmn_group_service',     native: false },

  // ── DMN — Escena C · table ──────────────────────────────────────────────
  '➕ Agregar regla':            { intent: 'dmn_add_rule',             native: true  },
  '🧩 Completar cobertura':      { intent: 'dmn_fill_coverage',        native: false },
  '🎚️ Sugerir hit policy':       { intent: 'dmn_suggest_policy',       native: false },
  '🗣️ Texto → FEEL':             { intent: 'dmn_nl_to_feel',           native: false },
  // Escena C · literal
  '🛠️ Corregir sintaxis':        { intent: 'dmn_fix_feel',             native: false },
  '💬 Explicar expresión':       { intent: 'explain',                  native: false },
  // Escena C · context
  '➕ Agregar entrada':          { intent: 'dmn_ctx_add_entry',        native: true  },
  '🗣️ Texto → entradas':         { intent: 'dmn_nl_to_context',        native: false },
  '💬 Explicar contexto':        { intent: 'explain',                  native: false },
  // Escena C · list
  '➕ Agregar item':             { intent: 'dmn_list_add',             native: true  },
  '🗣️ Texto → items':            { intent: 'dmn_nl_to_list',           native: false },
  '💬 Explicar lista':           { intent: 'explain',                  native: false },
  // Escena C · invocation
  '🔗 Sugerir bindings':         { intent: 'dmn_suggest_bindings',     native: false },
  '💬 Explicar invocación':      { intent: 'explain',                  native: false },
  // Escena C · relation
  '➕ Agregar fila':             { intent: 'dmn_rel_add_row',          native: true  },
  '📊 Agregar columna':          { intent: 'dmn_rel_add_col',          native: true  },
  '💬 Explicar relación':        { intent: 'explain',                  native: false },
  // Escena C · function
  '📥 Definir parámetros':       { intent: 'dmn_fn_params',            native: false },
  '🗣️ Texto → cuerpo FEEL':      { intent: 'dmn_nl_to_feel',           native: false },
  '💬 Explicar función':         { intent: 'explain',                  native: false },
  // Escena C · ml (THEMIS_ML_DECISION_PLAN §6.2)
  '💬 Explicar decisión ML':         { intent: 'dmn_ml_explain',           native: false },
  '🔗 Sugerir bindings desde DRD':   { intent: 'dmn_ml_suggest_bindings',  native: true  },
  '🧪 Sugerir fallback':             { intent: 'dmn_ml_suggest_fallback',  native: false },
  '📊 Generar tabla equivalente':    { intent: 'dmn_ml_to_table',          native: false },
  '🩺 Validar configuración':        { intent: 'validate',                 native: false },
};

/**
 * Pick the chip bucket for the rules-mode UI based on the active view, the
 * current selection (only used in DRD), and — when the boxed expression
 * editor is open — the kind of expression the user is editing.
 *
 * @param {Array}  selection
 * @param {{view: 'drd'|'table'|'expression', exprKind?: string}} dmnContext
 */
function _pickDmnBucket(selection, dmnContext = {}) {
  const view = dmnContext.view || 'drd';

  if (view === 'table') return CHIPS.dmn_table;

  if (view === 'expression') {
    const map = {
      literal:       CHIPS.dmn_literal,
      context:       CHIPS.dmn_context,
      list:          CHIPS.dmn_list,
      invocation:    CHIPS.dmn_invocation,
      relation:      CHIPS.dmn_relation,
      function:      CHIPS.dmn_function,
      decisionTable: CHIPS.dmn_table,
      ml:            CHIPS.dmn_ml,
    };
    return map[dmnContext.exprKind] || CHIPS.dmn_literal;
  }

  // view === 'drd'
  if (!selection || !selection.length) return CHIPS.dmn_none;
  if (selection.length > 1)             return CHIPS.dmn_multiple;
  return _pickDmnNodeBucket(selection[0]);
}

function _pickDmnNodeBucket(el) {
  if (!el) return CHIPS.dmn_none;

  // Prefer the moddle $type, then the LF-style type, then the kind label
  // exposed by the DMN model bridge (`decision`/`inputData`/…).
  const bo   = el.businessObject || {};
  const type = (bo.$type || el.type || '').toString();
  const kind = el.kind || bo.$kind || '';

  if (type.endsWith(':Decision')              || kind === 'decision')              return _pickDecisionBucket(el);
  if (type.endsWith(':InputData')             || kind === 'inputData')             return CHIPS.dmn_input;
  if (type.endsWith(':BusinessKnowledgeModel')|| kind === 'businessKnowledgeModel') return CHIPS.dmn_bkm;
  if (type.endsWith(':KnowledgeSource')       || kind === 'knowledgeSource')       return CHIPS.dmn_knowledgeSource;
  if (type.endsWith(':TextAnnotation')        || kind === 'textAnnotation')        return CHIPS.dmn_annotation;

  return CHIPS.dmn_decision;
}

function _pickDecisionBucket(el) {
  // ML decisions get their own catalogue when selected from the DRD too,
  // so the user can act on the Machine Learning logic without first opening
  // the boxed-expression editor.
  const bo = el && el.businessObject;
  const dl = bo && bo.decisionLogic;
  if (dl && dl.$type === 'epx:MachineLearningDecision') return CHIPS.dmn_ml;
  // The selection snapshot may only carry the inline decisionLogic shape.
  if (el && el.decisionLogic && el.decisionLogic.$type === 'epx:MachineLearningDecision') {
    return CHIPS.dmn_ml;
  }
  return CHIPS.dmn_decision;
}

/**
 * Returns chips for the current selection / view / expression kind.
 *
 * @param {Array|{selection?: Array}} selection
 *   Either the selection array itself (legacy callers) or an envelope
 *   `{ selection }` so unit tests / call sites can pass extra context
 *   without breaking the signature.
 * @param {string} [mode='bpmn']  'bpmn' | 'rules'
 * @param {object} [dmnContext]   `{ view, exprKind }` — only used when mode === 'rules'.
 * @returns {string[]}
 */
export function getChips(selection, mode = 'bpmn', dmnContext = undefined) {
  // Tolerate `{ selection: [...] }` envelopes for ergonomic tests.
  let sel = selection;
  if (sel && !Array.isArray(sel) && Array.isArray(sel.selection)) {
    sel = sel.selection;
  }

  if (mode === 'rules') return _pickDmnBucket(sel || [], dmnContext || {});

  if (!sel || sel.length === 0) return CHIPS.none;
  if (sel.length > 1)            return CHIPS.multiple;

  const type = sel[0].type || '';
  if (type.includes('Gateway'))                       return CHIPS.gateway;
  if (type.includes('Flow') || type.includes('Edge')) return CHIPS.flow;
  if (type.includes('Event'))                         return CHIPS.event;
  return CHIPS.task;
}

/**
 * Returns the intent for a given chip label.
 * @param {string} label
 * @returns {string}
 */
export function getIntentForChip(label) {
  const entry = CHIP_INTENTS[label];
  if (!entry) return 'chat';
  // Back-compat: callers historically expected a string.
  return typeof entry === 'string' ? entry : entry.intent;
}

/**
 * Returns true when the chip / intent must be dispatched natively (no /assist).
 * Intent strings are kept here too so callers that already mapped a label →
 * intent (e.g. via `detectIntent`) can decide without re-resolving the chip.
 *
 * @param {string} intent
 * @returns {boolean}
 */
export function isNativeIntent(intent) {
  if (!intent) return false;
  // Direct lookup by intent name across all chip entries.
  for (const entry of Object.values(CHIP_INTENTS)) {
    if (entry && entry.intent === intent && entry.native) return true;
  }
  return false;
}

/**
 * Returns the metadata `{ intent, native }` registered for a chip label.
 * Useful in tests; callers should prefer `getIntentForChip` + `isNativeIntent`.
 */
export function getChipMeta(label) {
  const entry = CHIP_INTENTS[label];
  if (!entry) return { intent: 'chat', native: false };
  if (typeof entry === 'string') return { intent: entry, native: false };
  return { intent: entry.intent, native: !!entry.native };
}
